import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useWorkTemplate, useCreateWorkTemplate, useUpdateWorkTemplate, useInstantiateWorkTemplate } from '@/hooks/use-work';
import { ProjectSearchComboBox } from '@/components/shared/project-search-combo-box';
import type { WorkStepTemplateItem } from '@/lib/types';
import type { ProjectOption } from '@/components/shared/project-search-combo-box';

const emptyItem = (order: number): WorkStepTemplateItem => ({
  id: 0,
  organization: 0,
  template: 0,
  order,
  title: '',
  result_mode: 'done',
  created_at: '',
  updated_at: '',
});

export function WorkTemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const numericId = isNew ? 0 : parseInt(id || '0', 10);

  const { data, isLoading, isError } = useWorkTemplate(isNew ? 'new' : numericId);
  const createMutation = useCreateWorkTemplate();
  const updateMutation = useUpdateWorkTemplate();
  const instantiateMutation = useInstantiateWorkTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<WorkStepTemplateItem[]>([emptyItem(1)]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);

  useEffect(() => {
    if (data) {
      setName(data.name);
      setDescription(data.description);
      const sorted = [...data.items].sort((a, b) => a.order - b.order || a.id - b.id);
      setItems(sorted.length ? sorted : [emptyItem(1)]);
    }
  }, [data]);

  const handleAddItem = () => {
    setItems((prev) => [...prev, emptyItem(prev.length > 0 ? Math.max(...prev.map((i) => i.order)) + 1 : 1)]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof WorkStepTemplateItem, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next.map((item, i) => ({ ...item, order: i + 1 }));
    });
  };

  const payloadItems = items.map((item, i) => ({
    ...(item.id > 0 ? { id: item.id } : {}),
    order: i + 1,
    title: item.title,
    result_mode: item.result_mode,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isNew) {
        await createMutation.mutateAsync({
          name,
          description,
          items: payloadItems,
        });
      } else {
        await updateMutation.mutateAsync({
          id: numericId,
          data: { name, description, items: payloadItems },
        });
      }
      navigate('/work/templates');
    } catch {
      // error handled by mutation
    }
  };

  const handleInstantiate = async () => {
    if (!numericId || !selectedProject) return;
    await instantiateMutation.mutateAsync({
      id: numericId,
      data: { project: selectedProject.id },
    });
    navigate('/work/templates');
  };

  if (!isNew && isLoading) {
    return <div className="p-8">Loading...</div>;
  }
  if (!isNew && isError) {
    return <div className="p-8 text-red-600">Failed to load template.</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/work/templates')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? 'New Step Template' : name || 'Edit Step Template'}
          </h1>
          <p className="text-gray-600">
            {isNew
              ? 'Create a reusable checklist with ordered steps.'
              : 'Edit template details and step order.'}
          </p>
        </div>
      </div>

      {!isNew && numericId > 0 && (
        <Card
          id="work-template-instantiation-fields"
          data-required-validation-boundary="true"
        >
          <CardHeader>
            <CardTitle>Instantiate on project</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2 items-end">
            <div className="flex-1">
              <Label>Project *</Label>
              <ProjectSearchComboBox
                value={selectedProject}
                onChange={setSelectedProject}
                required
              />
            </div>
            <Button
              onClick={handleInstantiate}
              data-validation-scope="work-template-instantiation-fields"
              disabled={instantiateMutation.isPending}
            >
              {instantiateMutation.isPending ? 'Creating...' : 'Instantiate'}
            </Button>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Template Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Truss Rigging Inspection"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Describe when to use this template..."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Steps</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
              <Plus className="h-4 w-4 mr-1" />
              Add Step
            </Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Order</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-40">Result Mode</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id || `new-${index}`}>
                      <TableCell className="align-middle">{index + 1}</TableCell>
                      <TableCell>
                        <Input
                          value={item.title}
                          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                          placeholder="Step title"
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={item.result_mode}
                          onValueChange={(value) =>
                            handleItemChange(index, 'result_mode', value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pass_fail">Pass / Fail</SelectItem>
                            <SelectItem value="done">Done / Not Done</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={index === 0}
                            onClick={() => moveItem(index, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={index === items.length - 1}
                            onClick={() => moveItem(index, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={items.length <= 1}
                            onClick={() => handleRemoveItem(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/work/templates')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Saving...'
              : isNew
              ? 'Create Template'
              : 'Save Template'}
          </Button>
        </div>
      </form>
    </div>
  );
}
